/**
 * DemoTour (iter-203) — checklist + coachmark UI for demo users.
 *
 * Renders three pieces:
 *   1. A floating, minimizable Checklist anchored bottom-right with
 *      progress bar, ordered steps, ✓ for completed, → for the active
 *      step.
 *   2. A Coachmark popover that highlights the DOM element matching
 *      ``data-tour-id="<step.target>"`` whenever the user clicks a
 *      step in the checklist. The popover is dismissed by clicking
 *      its primary CTA (which also navigates to ``step.cta_route``
 *      and marks the step as completed) or by closing it.
 *   3. A small floating "?" FAB to reopen the checklist after it's
 *      minimized.
 *
 * Mount inside <Layout> after <DemoBanner>; it self-mutes when the
 * user isn't on a demo or when the tour is completed.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, ChevronRight, HelpCircle, Minus, Sparkles, X } from "lucide-react";
import * as Lucide from "lucide-react";
import { useDemoTour } from "./DemoTourProvider";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";

function StepIcon({ name, className }) {
  const Icon = Lucide[name] || Sparkles;
  return <Icon className={className} />;
}

function Coachmark({ step, onClose, onAccept }) {
  const [anchor, setAnchor] = useState(null);

  useEffect(() => {
    if (!step?.target) return undefined;
    const find = () => {
      const sel = step.target;
      // Try data-tour-id first, fall back to data-testid (most of our
      // existing nav/CTA elements already expose a stable testid).
      const el =
        document.querySelector(`[data-tour-id="${sel}"]`) ||
        document.querySelector(`[data-testid="${sel}"]`);
      setAnchor(el || null);
    };
    find();
    // Re-find on resize / route change quirks.
    const t = setInterval(find, 600);
    window.addEventListener("resize", find);
    window.addEventListener("scroll", find, true);
    return () => {
      clearInterval(t);
      window.removeEventListener("resize", find);
      window.removeEventListener("scroll", find, true);
    };
  }, [step?.target]);

  const position = useMemo(() => {
    if (!anchor) {
      // No target on this route — center the card.
      return {
        position: "fixed",
        left: "50%",
        bottom: 110,
        transform: "translateX(-50%)",
      };
    }
    const rect = anchor.getBoundingClientRect();
    const placeBelow = rect.bottom + 240 < window.innerHeight;
    return {
      position: "fixed",
      left: Math.max(12, Math.min(window.innerWidth - 320, rect.left)),
      top: placeBelow ? rect.bottom + 8 : Math.max(12, rect.top - 240),
    };
  }, [anchor]);

  return (
    <>
      {/* Highlight ring on the anchor (skipped when no anchor). */}
      {anchor && (
        <div
          aria-hidden
          className="pointer-events-none fixed z-[60] rounded-xl ring-4 ring-emerald-500/70 ring-offset-2 ring-offset-white/60 animate-pulse"
          style={{
            left: anchor.getBoundingClientRect().left - 4,
            top: anchor.getBoundingClientRect().top - 4,
            width: anchor.getBoundingClientRect().width + 8,
            height: anchor.getBoundingClientRect().height + 8,
          }}
          data-testid="demo-coachmark-highlight"
        />
      )}
      <div
        className="z-[61] w-[300px] max-w-[92vw] rounded-2xl bg-white shadow-2xl border border-emerald-200 p-4"
        style={position}
        data-testid="demo-coachmark"
      >
        <button /* design-system: ALLOW legacy-todo */
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-2 right-2 h-7 w-7 rounded-full hover:bg-slate-100 inline-flex items-center justify-center text-slate-500"
          data-testid="demo-coachmark-close"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="flex items-start gap-2.5">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 shrink-0">
            <StepIcon name={step.icon} className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <p className="font-semibold text-slate-900 text-sm leading-snug">{step.title}</p>
            <p className="text-xs text-slate-600 mt-1 leading-snug">{step.description}</p>
          </div>
        </div>
        <button /* design-system: ALLOW legacy-todo */
          type="button"
          onClick={onAccept}
          className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold py-2 transition-colors"
          data-testid="demo-coachmark-cta"
        >
          {step.cta_label}
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </>
  );
}

function ChecklistCard({ tour, currentStep, onPick, onMinimize, onDismiss, lang }) {
  const done = new Set(tour.state?.steps_completed || []);
  const total = tour.steps.length;
  const completed = tour.steps.filter((s) => done.has(s.id)).length;
  const allDone = completed >= total;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 w-[320px] max-w-[92vw] rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden"
      data-testid="demo-tour-checklist"
    >
      <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-4 py-3 flex items-center justify-between">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide font-bold opacity-80">
            {tx(lang, "Your demo tour", "Tu tour del demo", "Seu tour do demo")}
          </p>
          <p className="text-sm font-semibold truncate">
            {allDone
              ? tx(lang, "All set — nice work!", "Listo — ¡bien hecho!", "Pronto — bom trabalho!")
              : tx(lang, "5 quick wins", "5 logros rápidos", "5 conquistas rápidas")}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button /* design-system: ALLOW legacy-todo */
            type="button"
            onClick={onMinimize}
            aria-label="Minimize"
            className="h-7 w-7 rounded-full hover:bg-white/20 inline-flex items-center justify-center"
            data-testid="demo-tour-minimize"
          >
            <Minus className="h-4 w-4" />
          </button>
          <button /* design-system: ALLOW legacy-todo */
            type="button"
            onClick={onDismiss}
            aria-label="Close"
            className="h-7 w-7 rounded-full hover:bg-white/20 inline-flex items-center justify-center"
            data-testid="demo-tour-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="px-4 pt-3">
        <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full bg-emerald-500 transition-all"
            style={{ width: `${total ? (completed / total) * 100 : 0}%` }}
            data-testid="demo-tour-progress"
          />
        </div>
        <p className="text-[11px] text-slate-500 mt-1.5">
          {completed} / {total} · {tx(lang, "completed", "completados", "completos")}
        </p>
      </div>
      <ul className="px-2 py-2 space-y-0.5 max-h-[55vh] overflow-y-auto">
        {tour.steps.map((s) => {
          const isDone = done.has(s.id);
          const isCurrent = currentStep?.id === s.id;
          return (
            <li key={s.id}>
              <button /* design-system: ALLOW legacy-todo */
                type="button"
                onClick={() => onPick(s)}
                className={[
                  "w-full text-left px-2.5 py-2 rounded-lg flex items-start gap-2 transition-colors",
                  isDone ? "bg-emerald-50/50" : isCurrent ? "bg-emerald-50 ring-1 ring-emerald-200" : "hover:bg-slate-50",
                ].join(" ")}
                data-testid={`demo-tour-step-${s.id}`}
              >
                <span className={[
                  "inline-flex h-6 w-6 items-center justify-center rounded-full shrink-0 text-xs",
                  isDone ? "bg-emerald-500 text-white" : isCurrent ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500",
                ].join(" ")}>
                  {isDone ? <CheckCircle2 className="h-3.5 w-3.5" /> : <StepIcon name={s.icon} className="h-3.5 w-3.5" />}
                </span>
                <span className="min-w-0">
                  <span className={["block text-sm font-semibold leading-snug", isDone ? "text-slate-500 line-through" : "text-slate-900"].join(" ")}>
                    {s.title}
                  </span>
                  {!isDone && (
                    <span className="block text-[11px] text-slate-500 leading-snug mt-0.5">
                      {s.description}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function DemoTour() {
  const { tour, currentStep, markStep, minimize, reopen, dismiss } = useDemoTour();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(null);

  if (!tour?.is_demo || !tour?.steps?.length) return null;

  const minimized = !!tour.state?.minimized;
  const completed = (tour.state?.steps_completed || []).length;
  const total = tour.steps.length;
  const allDone = completed >= total;

  // FAB when minimized — clicking it reopens the checklist.
  if (minimized) {
    return (
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        onClick={reopen}
        aria-label={tx(lang, "Reopen demo tour", "Reabrir tour del demo", "Reabrir tour do demo")}
        className="fixed bottom-4 right-4 z-50 h-12 px-4 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg inline-flex items-center gap-2 text-sm font-semibold"
        data-testid="demo-tour-fab"
      >
        <HelpCircle className="h-4 w-4" />
        {allDone
          ? tx(lang, "Tour done", "Tour completo", "Tour completo")
          : `${completed}/${total} · ${tx(lang, "tour", "tour", "tour")}`}
      </button>
    );
  }

  const handlePick = (s) => {
    setActiveStep(s);
  };
  const handleAccept = async () => {
    if (!activeStep) return;
    await markStep(activeStep.id);
    if (activeStep.cta_route) navigate(activeStep.cta_route);
    setActiveStep(null);
  };

  return (
    <>
      <ChecklistCard
        tour={tour}
        currentStep={currentStep}
        onPick={handlePick}
        onMinimize={minimize}
        onDismiss={dismiss}
        lang={lang}
      />
      {activeStep && (
        <Coachmark
          step={activeStep}
          onClose={() => setActiveStep(null)}
          onAccept={handleAccept}
        />
      )}
    </>
  );
}

export default DemoTour;
